import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import { RichHtmlEditor } from '@/components/rich-html-editor';
import { useCguVersion, useCguVersions } from '@/api/use-legal-admin';
import {
  diffBlocks,
  htmlToBlocks,
  isIdenticalDiff,
  type CguDiffBlock,
  type CguDiffKind,
} from '@/routes/admin/cgu/cgu-diff';
import { formatDate, stateBadgeVariant, stateLabel } from '@/routes/admin/cgu/cgu-format';

// =============================================================================
// cgu-comparison.tsx — Mode comparaison côte-à-côte brouillon ⟷ version publiée (R15-GOV-05).
// =============================================================================
// DBU §2.9 l.2048 : « Mode comparaison côte-à-côte : affichage simultané du brouillon en cours et de
// la version courante publiée, à des fins de relecture des écarts. » l.1987 : « Vue côte-à-côte du
// brouillon en cours d'édition et de la version courante publiée, à des fins de comparaison. »
//
// COMPOSITION FRONT (décision GATE_HUMAN D4, option a) : la vue est ASSEMBLÉE À PARTIR DES LECTURES
// DÉJÀ SERVIES par le BC legal — `useCguVersions` (registre) pour localiser le Brouillon et la Publiée
// courants, puis `useCguVersion` (versionDetail) pour CHACUN. AUCUN endpoint combiné, aucun contrat
// nouveau, aucun back. Le front n'est qu'un assembleur read-only.
//
// RENDU sûr : le corps HTML (déjà nettoyé serveur) est rendu via `RichHtmlEditor` en lecture seule
// (disabled) — même choke point d'affichage que le détail (jamais de dangerouslySetInnerHTML).
//
// ÉCARTS VISIBLES : `cgu-diff` extrait les blocs texte des deux corps et calcule un diff LCS ; chaque
// écart est étiqueté par un LIBELLÉ (WCAG 1.4.1 — la couleur n'est qu'un renfort, jamais seule).

/** No-op stable : les éditeurs de comparaison sont en lecture seule (aucun write). */
const READ_ONLY_NOOP = (): void => {};

/** Métadonnées d'affichage d'un écart : libellé texte (porteur) + variante de badge + bordure. */
const DIFF_META: Record<
  CguDiffKind,
  { readonly label: string; readonly variant: 'default' | 'refused' | 'outline'; readonly border: string }
> = {
  added: {
    label: 'Ajouté au brouillon',
    variant: 'default',
    border: 'border-l-2 border-primary',
  },
  removed: {
    label: 'Retiré depuis la version publiée',
    variant: 'refused',
    border: 'border-l-2 border-destructive',
  },
  unchanged: {
    label: 'Inchangé',
    variant: 'outline',
    border: 'border-l-2 border-border',
  },
};

export function CguComparison(): React.JSX.Element {
  const versions = useCguVersions();
  const rows = versions.data ?? [];
  const draftRow = rows.find((v) => v.state === 'Brouillon');
  const publishedRow = rows.find((v) => v.state === 'Publiee');

  // Hooks appelés inconditionnellement (règles des hooks) ; désactivés tant que l'id est absent.
  const draft = useCguVersion(draftRow?.cguVersionId);
  const published = useCguVersion(publishedRow?.cguVersionId);

  if (versions.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement du registre…
      </p>
    );
  }
  if (versions.isError) {
    return (
      <p role="alert" className="text-destructive">
        Erreur de chargement : {versions.error.message}
      </p>
    );
  }

  if (draftRow === undefined || publishedRow === undefined) {
    return (
      <div className="space-y-2">
        <h2 className="text-xl font-semibold tracking-tight">Comparaison côte-à-côte</h2>
        <p role="status" className="text-muted-foreground">
          {draftRow === undefined && publishedRow === undefined
            ? 'La comparaison requiert un brouillon en cours ET une version publiée courante. Aucun des deux n’existe pour le moment.'
            : draftRow === undefined
              ? 'Aucun brouillon en cours. Créez un brouillon pour le comparer à la version publiée courante.'
              : 'Aucune version publiée courante. Publiez une version pour disposer d’une référence de comparaison.'}
        </p>
      </div>
    );
  }

  if (draft.isPending || published.isPending) {
    return (
      <p role="status" className="text-muted-foreground">
        Chargement des contenus à comparer…
      </p>
    );
  }
  if (draft.isError || published.isError) {
    const message = draft.error?.message ?? published.error?.message ?? 'erreur inconnue.';
    return (
      <p role="alert" className="text-destructive">
        Erreur de chargement des contenus : {message}
      </p>
    );
  }

  const draftVersion = draft.data;
  const publishedVersion = published.data;

  // Diff : base = version PUBLIÉE (avant), nouveau = BROUILLON (après).
  const diff = diffBlocks(
    htmlToBlocks(publishedVersion.contentHtml),
    htmlToBlocks(draftVersion.contentHtml),
  );
  const identical = isIdenticalDiff(diff);
  const addedCount = diff.filter((b) => b.kind === 'added').length;
  const removedCount = diff.filter((b) => b.kind === 'removed').length;

  return (
    <section
      className="space-y-8"
      aria-label="Comparaison côte-à-côte du brouillon et de la version publiée"
    >
      <header className="space-y-1">
        <h2 className="text-xl font-semibold tracking-tight">Comparaison côte-à-côte</h2>
        <p className="text-muted-foreground">
          Le brouillon en cours d’édition et la version courante publiée, affichés simultanément pour
          la relecture des écarts.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-2">
        <ComparisonColumn
          headingId="cgu-compare-draft-heading"
          roleLabel="Brouillon en cours d’édition"
          versionNumber={draftVersion.versionNumber}
          state={draftVersion.state}
          effectiveDate={draftVersion.effectiveDate}
          contentHtml={draftVersion.contentHtml ?? ''}
          editorId="cgu-compare-draft"
          editorLabel="Contenu du brouillon en cours"
          editorHint="Version en préparation, non encore opposable."
        />
        <ComparisonColumn
          headingId="cgu-compare-published-heading"
          roleLabel="Version publiée courante"
          versionNumber={publishedVersion.versionNumber}
          state={publishedVersion.state}
          effectiveDate={publishedVersion.effectiveDate}
          contentHtml={publishedVersion.contentHtml ?? ''}
          editorId="cgu-compare-published"
          editorLabel="Contenu de la version publiée courante"
          editorHint="Version actuellement en vigueur et opposable."
        />
      </div>

      <section className="space-y-3" aria-label="Écarts détectés entre le brouillon et la version publiée">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="text-base font-semibold tracking-tight">Écarts détectés</h3>
          <p role="status" className="text-sm text-muted-foreground">
            {identical
              ? 'Aucune différence : le brouillon est identique à la version publiée.'
              : `${String(addedCount)} bloc${addedCount > 1 ? 's' : ''} ajouté${
                  addedCount > 1 ? 's' : ''
                }, ${String(removedCount)} bloc${removedCount > 1 ? 's' : ''} retiré${
                  removedCount > 1 ? 's' : ''
                }.`}
          </p>
        </div>

        {identical ? (
          <p className="text-sm text-muted-foreground">
            Les deux versions présentent le même contenu éditorial.
          </p>
        ) : (
          <ol className="space-y-2">
            {diff.map((block, index) => (
              <DiffRow key={`${String(index)}-${block.kind}`} block={block} />
            ))}
          </ol>
        )}
      </section>
    </section>
  );
}

interface ComparisonColumnProps {
  readonly headingId: string;
  readonly roleLabel: string;
  readonly versionNumber: string;
  readonly state: 'Brouillon' | 'Publiee' | 'Archivee';
  readonly effectiveDate: string | null;
  readonly contentHtml: string;
  readonly editorId: string;
  readonly editorLabel: string;
  readonly editorHint: string;
}

/** Une colonne de la comparaison : en-tête (rôle + n° + état + date) puis contenu en lecture seule. */
function ComparisonColumn({
  headingId,
  roleLabel,
  versionNumber,
  state,
  effectiveDate,
  contentHtml,
  editorId,
  editorLabel,
  editorHint,
}: ComparisonColumnProps): React.JSX.Element {
  return (
    <article aria-labelledby={headingId} className="space-y-3 rounded-md border border-border p-4">
      <div className="space-y-1">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {roleLabel}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <h3 id={headingId} className="text-lg font-semibold tracking-tight">
            {versionNumber}
          </h3>
          <Badge variant={stateBadgeVariant(state)}>{stateLabel(state)}</Badge>
        </div>
        <p className="text-sm text-muted-foreground">
          Entrée en vigueur : {formatDate(effectiveDate)}
        </p>
      </div>
      <RichHtmlEditor
        value={contentHtml}
        onChange={READ_ONLY_NOOP}
        disabled
        id={editorId}
        label={editorLabel}
        hint={editorHint}
      />
    </article>
  );
}

/** Une ligne d'écart : libellé texte porteur (WCAG 1.4.1) + renfort couleur (badge + bordure). */
function DiffRow({ block }: { readonly block: CguDiffBlock }): React.JSX.Element {
  const meta = DIFF_META[block.kind];
  return (
    <li className={`flex flex-col gap-1 rounded-r-md bg-muted/30 px-3 py-2 sm:flex-row sm:items-start sm:gap-3 ${meta.border}`}>
      <Badge variant={meta.variant} className="shrink-0">
        {meta.label}
      </Badge>
      <span className="text-sm text-foreground">{block.text}</span>
    </li>
  );
}
